// El equipo tal como lo ve quien mira, calculado en UN sitio: su bandeja, los bloques de la lista (con «Con Santi», las
// subidas en la fila de su agente y los aparcados), el orden plano y quién le necesita. Lo leen la lista, el riel
// plegado, «Saltar a…» y Alt+↑/↓ del chat: antes cada uno lo calculaba a su manera y el teclado se saltaba filas (04-11,
// auditoría de código B1/B2).
//
// El chat abierto conserva su sitio: al abrirlo el servidor lo marca leído y, con «lo no leído primero», su fila
// saltaría bajo el cursor. El almacén guarda cómo estaba al abrirlo (`noLeidoAlAbrir`) y todos ordenan con eso.
//
// Aparcar depende del reloj (24 h sin hablar, 7 días aparcado): en vez de sondear, UN temporizador al próximo cambio.
//
// 05-11 (QA G3): con el puntero sobre la lista el orden se queda quieto (ordenQuieto.ts): la foto se aplica AQUÍ, encima
// de los bloques vivos, así que la lista, el riel, Ctrl+K y Alt+↑/↓ siguen viendo el mismo orden (el que se ve).
import { useEffect, useMemo, useState } from 'react';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { useSubidas } from '../despliegues/store';
import { useSantiPorAgente } from '../pelotas/porAgente';
import { useOficinaStore } from '../store';
import { aplicarOrdenQuieto, useOrdenQuieto } from './ordenQuieto';
import { agruparPorSituacion, proximoCambioDe, type SantiPorAgente } from './situacion';
import { useBandeja } from './useBandeja';

/** setTimeout no aguanta más de ~24,8 días. */
const MAXIMO_TEMPORIZADOR_MS = 2 ** 31 - 1;

export function useEquipo(contratados: Record<string, ContratadoDTO>) {
  const santi = useSantiPorAgente();
  const { porAgente: subidas } = useSubidas();
  // La hora se calcula sobre TODOS (no solo la bandeja que se ve): así las pestañas de las bandejas cuentan «te toca» con
  // el mismo reloj que la lista (05-11, auditoría A; antes cada pestaña miraba Date.now() por su cuenta).
  const todos = useMemo(() => Object.values(contratados), [contratados]);
  const ahora = useRelojAparcar(todos, santi, subidas);
  const bandeja = useBandeja(contratados, subidas, ahora);
  const congelado = useOficinaStore((s) => s.noLeidoAlAbrir);
  const { visibles, clave } = bandeja;
  const foto = useOrdenQuieto((s) => s.foto);
  const grupos = useMemo(
    () => aplicarOrdenQuieto(agruparPorSituacion(visibles, (c) => (congelado && c.agentId === congelado.agentId ? congelado.noLeido : c.noLeido), santi, { subidas, ahora }), foto, clave),
    [visibles, congelado, santi, subidas, ahora, foto, clave],
  );
  const orden = useMemo(() => grupos.flatMap((g) => g.miembros), [grupos]);
  // «Te necesita» = está en «Te toca» (situacion.ts `necesitaAPau`), con el mismo contexto que la lista.
  const necesita = useMemo(() => {
    const ids = new Set(grupos.find((g) => g.bloque === 'te_toca')?.miembros.map((c) => c.agentId));
    return (c: Pick<ContratadoDTO, 'agentId'>) => ids.has(c.agentId);
  }, [grupos]);
  return { ...bandeja, santi, subidas, grupos, orden, necesita, ahora };
}

/** La hora con la que se mira el equipo: se adelanta sola justo cuando alguien se aparca o pasa a «proponer cerrar». */
export function useRelojAparcar(visibles: readonly ContratadoDTO[], santi: SantiPorAgente, subidas: ReadonlyMap<string, { creadoEn: string }>): number {
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const t0 = Date.now();
    let proximo = Number.POSITIVE_INFINITY;
    for (const c of visibles) {
      const p = proximoCambioDe(c, santi.get(c.agentId), { subida: subidas.get(c.agentId) ?? null, ahora: t0 });
      if (p !== null && p < proximo) proximo = p;
    }
    if (!Number.isFinite(proximo)) return;
    // Un segundo de margen: al despertar, el cambio ya ha pasado.
    const id = setTimeout(() => setAhora(Date.now()), Math.min(Math.max(0, proximo - t0) + 1000, MAXIMO_TEMPORIZADOR_MS));
    return () => clearTimeout(id);
  }, [visibles, santi, subidas, ahora]);
  return ahora;
}

/**
 * Alt+↓ / Alt+↑: el siguiente (o anterior) que te necesita en el orden de la lista, sin contar el abierto y dando la
 * vuelta. Con `todos`, el siguiente sin más (Alt+Mayús). null = no queda nadie (Nada más te toca).
 */
export function siguienteEnEquipo<C extends Pick<ContratadoDTO, 'agentId'>>(orden: readonly C[], desde: string | null, paso: 1 | -1, cuenta: (c: C) => boolean): C | null {
  const n = orden.length;
  if (!n) return null;
  const i = orden.findIndex((c) => c.agentId === desde);
  for (let k = 1; k <= n; k += 1) {
    const c = orden[(((i < 0 ? (paso === 1 ? -1 : 0) : i) + paso * k) % n + n) % n]!;
    if (c.agentId !== desde && cuenta(c)) return c;
  }
  return null;
}
